// src/components/etapaproductiva/RegistrarEProductiva.jsx

import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

// =====================================================
// Configuración del módulo
// =====================================================

const MODULO = "EtapaProductiva";

// =====================================================
// Configuración de autenticación
// =====================================================

const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : {},
  };
};

// =====================================================
// Helpers de normalización
// =====================================================

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

// =====================================================
// Lectura segura de permisos desde localStorage
// =====================================================

const obtenerObjetoStorage = () => {
  const claves = ["permisos", "usuario", "user", "usuarios"];

  for (const clave of claves) {
    try {
      const valor = localStorage.getItem(clave);

      if (valor) {
        return JSON.parse(valor);
      }
    } catch (error) {
      console.error(`Error leyendo ${clave}:`, error);
    }
  }

  return {};
};

const obtenerModulosStorage = () => {
  const permisosData = obtenerObjetoStorage();

  const modulos = Array.isArray(permisosData)
    ? permisosData
    : permisosData?.modulos ||
    permisosData?.permisos ||
    permisosData?.usuario?.modulos ||
    permisosData?.usuario?.permisos ||
    permisosData?.user?.modulos ||
    permisosData?.user?.permisos ||
    [];

  return Array.isArray(modulos) ? modulos : [];
};

// =====================================================
// Búsqueda del módulo actual
// =====================================================

const obtenerModuloPorNombre = (nombreModulo) => {
  const modulos = obtenerModulosStorage();
  const moduloBuscado = normalizarTexto(nombreModulo);

  return (
    modulos.find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.nombre ||
        modulo?.name ||
        "";

      return normalizarTexto(nombre) === moduloBuscado;
    }) || {}
  );
};

// =====================================================
// Validación de acciones por permiso
// =====================================================

const ACCIONES_PERMISOS = {
  leer: {
    campo: "puedeLeer",
    alias: ["leer", "ver", "listar", "read", "view"],
  },

  crear: {
    campo: "puedeCrear",
    alias: ["crear", "create", "add", "registrar"],
  },

  actualizar: {
    campo: "puedeActualizar",
    alias: ["actualizar", "editar", "update", "edit"],
  },

  eliminar: {
    campo: "puedeEliminar",
    alias: ["eliminar", "delete", "remove", "borrar"],
  },
};

const tienePermisoAccion = (modulo = {}, accion = "") => {
  const accionLower = normalizarTexto(accion);
  const config = ACCIONES_PERMISOS[accionLower];

  if (!config) return false;

  const { campo, alias } = config;

  return (
    modulo?.[campo] === true ||
    modulo?.[accionLower] === true ||
    modulo?.acciones?.[accionLower] === true ||
    alias.some((nombreAccion) => {
      return (
        modulo?.[nombreAccion] === true ||
        modulo?.acciones?.[nombreAccion] === true
      );
    })
  );
};





// ===== Helpers de fecha =====
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return { y, m, d };
};

const fmtISO = (y, m, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const addMonthsClamped = (isoDate, months) => {
  const p = parseISO(isoDate);
  if (!p) return "";

  const base = new Date(p.y, p.m - 1 + months, 1);
  const lastDay = new Date(
    base.getFullYear(),
    base.getMonth() + 1,
    0
  ).getDate();

  const day = Math.min(p.d, lastDay);

  return fmtISO(base.getFullYear(), base.getMonth() + 1, day);
};

const toISODateUTC = (v) =>
  v ? new Date(`${v}T00:00:00.000Z`).toISOString() : undefined;

const toNumberOrUndef = (v) =>
  v !== "" && !Number.isNaN(Number(v)) ? Number(v) : undefined;

const RegistrarEProductiva = () => {
  const navigate = useNavigate();
  const { aprendizId, matriculaId, epracticaId } = useParams();

  // =======================
  // MODO EDICIÓN
  // =======================
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    setEdit(!!epracticaId);
  }, [epracticaId]);

  const moduloEtapaProductiva = useMemo(
    () => obtenerModuloPorNombre(MODULO),
    []
  );


  const puedeCrear = useMemo(
    () => tienePermisoAccion(moduloEtapaProductiva, "crear"),
    [moduloEtapaProductiva]
  );


  // =======================
  // DATOS INDIVIDUALES
  // =======================
  const [aprendizInfo, setAprendizInfo] = useState(null);
  const [matriculasOptions, setMatriculasOptions] = useState([]);
  const [loadingMatriculas, setLoadingMatriculas] = useState(false);

  const [documentoAprendizUI, setDocumentoAprendizUI] = useState("");
  const [matriculaUI, setMatriculaUI] = useState("");

  const [aprendizObjectId, setAprendizObjectId] = useState("");
  const [documentoAprendiz, setDocumentoAprendiz] = useState("");
  const [matriculaObjectId, setMatriculaObjectId] = useState("");
  const [codMatricula, setCodMatricula] = useState("");

  // =======================
  // INSTRUCTOR / ENTE / JEFE
  // =======================
  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [documentoInstructor, setDocumentoInstructor] = useState("");

  const [enteObjectId, setEnteObjectId] = useState("");
  const [nitEnteconformador, setNitEnteconformador] = useState("");

  const [jefeObjectId, setJefeObjectId] = useState("");
  const [documentoJefe, setDocumentoJefe] = useState("");

  const [nombreEnte, setNombreEnte] = useState("");
  const [enteBloqueadoPorAsociacion, setEnteBloqueadoPorAsociacion] =
    useState(false);

  const [nombreJefe, setNombreJefe] = useState("");
  const [estadoAsignacion, setEstadoAsignacion] = useState("");
  const [mensajeAsociacion, setMensajeAsociacion] = useState("");
  const [telefono1Jefe, setTelefono1Jefe] = useState("");
  const [correoJefe, setCorreoJefe] = useState("");

  const [instructorNombre, setInstructorNombre] = useState("");
  const [instructorEmail, setInstructorEmail] = useState("");
  const [instructorId, setInstructorId] = useState("");

  // =======================
  // ETAPA PRÁCTICA
  // =======================
  const [estadoEtapaPractica] = useState("");
  const [fechaInicioEtapaPractica, setFechaInicioEtapaPractica] = useState("");
  const [fechaFinEtapaPractica, setFechaFinEtapaPractica] = useState("");
  const [clasificacionEtapaPractica, setClasificacionEtapaPractica] =
    useState("publica");

  const [alternativaEtapaPractica, setAlternativaEtapaPractica] = useState("");
  const [subtipoAlternativaEtapaPractica, setSubtipoAlternativaEtapaPractica] =
    useState("");

  const [alternativa, setAlternativa] = useState("");
  const [subtipo, setSubtipo] = useState("");

  // =======================
  // ARL / EPP
  // =======================
  const [afiliadoARL, setAfiliadoARL] = useState("");
  const [nivelRiesgoARL, setNivelRiesgoARL] = useState("");
  const [riesgoCorrespondeARL, setRiesgoCorrespondeARL] = useState("");
  const [cuentaEPP, setCuentaEPP] = useState("");

  // =======================
  // ALTERNATIVAS
  // =======================
  const alternativas = useMemo(
    () => ({
      "Proyecto Productivo": [
        "Sena - Empresa",
        "Sena Proveedor Sena",
        "Producción de Centros",
        "Proyecto productivo I+D+i",
        "Proyectos Ruta Emprendedora SENA",
        "Prácticas en economía popular/campesina",
        "Convocatorias SENNOVA",
      ],
      Monitoría: [
        "Monitoría regular",
        "Monitoría convocatorias SENNOVA",
        "Otra",
      ],
      "Contrato Vínculo Formativo": [
        "Apoyo a una unidad productiva familiar",
        "De apoyo a institución estatal/ONG/sin ánimo de lucro",
        "Asesoría a pymes",
        "Prácticas en economía popular/campesina",
      ],
      "Contrato de Aprendizaje": [
        "Contrato de aprendizaje (regular)",
        "Convocatorias SENNOVA",
        "Prácticas en economía popular/campesina",
      ],
      "Vínculo Laboral": [
        "Vínculo laboral (regular)",
        "Prácticas en economía popular/campesina",
      ],
    }),
    []
  );

  // =======================
  // PRELLENAR IDS DESDE URL
  // =======================
  useEffect(() => {
    if (aprendizId && !edit) {
      setAprendizObjectId(aprendizId);
    }
  }, [aprendizId, edit]);

  useEffect(() => {
    if (matriculaId && !edit) {
      setMatriculaObjectId(matriculaId);
    }
  }, [matriculaId, edit]);

  // =======================
  // CARGAR DATOS PARA EDITAR
  // =======================
  const getData = useCallback(async () => {
    try {
      if (!epracticaId) return;

      const { data } = await axios.get(
        `/api/etapapractica/listid/${epracticaId}`,
        authConfig()
      );

      if (!data?.ok) return;

      const detalle = data.etapa || {};

      const aprendizIdEdit =
        detalle.aprendizObjectId?._id || detalle.aprendizObjectId || "";

      setAprendizObjectId(String(aprendizIdEdit));

      const docAprendiz =
        detalle.documentoAprendiz != null
          ? String(detalle.documentoAprendiz)
          : "";

      setDocumentoAprendiz(docAprendiz);
      setDocumentoAprendizUI(docAprendiz);

      if (
        detalle.aprendizObjectId &&
        typeof detalle.aprendizObjectId === "object"
      ) {
        setAprendizInfo(detalle.aprendizObjectId);
      } else {
        setAprendizInfo({ documentoAprendiz: docAprendiz });
      }

      const matId =
        detalle.matriculaObjectId?._id || detalle.matriculaObjectId || "";

      setMatriculaObjectId(String(matId));

      const codMat =
        detalle.codMatricula != null ? String(detalle.codMatricula) : "";

      setCodMatricula(codMat);

      const ficha = detalle.matriculaObjectId?.fichaGrupo ?? "N/A";
      const anio = detalle.matriculaObjectId?.anioLectivo ?? "";

      setMatriculaUI(`Matrícula ${codMat || "N/A"} — Ficha ${ficha} — ${anio}`);

      setFechaInicioEtapaPractica(
        detalle.fechaInicioEtapaPractica
          ? new Date(detalle.fechaInicioEtapaPractica)
            .toISOString()
            .slice(0, 10)
          : ""
      );

      setFechaFinEtapaPractica(
        detalle.fechaFinEtapaPractica
          ? new Date(detalle.fechaFinEtapaPractica).toISOString().slice(0, 10)
          : ""
      );

      setClasificacionEtapaPractica(
        detalle.clasificacionEtapaPractica || "publica"
      );

      setAlternativaEtapaPractica(detalle.alternativaEtapaPractica || "");
      setSubtipoAlternativaEtapaPractica(
        detalle.subtipoAlternativaEtapaPractica || ""
      );
      setAlternativa(detalle.alternativaEtapaPractica || "");
      setSubtipo(detalle.subtipoAlternativaEtapaPractica || "");

      const enteId = detalle.enteObjectId?._id || detalle.enteObjectId || "";
      setEnteObjectId(String(enteId));

      setNombreEnte(
        detalle.enteObjectId?.nombreEnteconformador ||
        detalle.nombreEnteconformador ||
        ""
      );

      setNitEnteconformador(
        detalle.nitEnteconformador != null
          ? String(detalle.nitEnteconformador)
          : ""
      );

      const docInst =
        detalle.documentoInstructor != null
          ? String(detalle.documentoInstructor)
          : "";

      setDocumentoInstructor(docInst);

      const instId =
        detalle.instructorObjectId?._id || detalle.instructorObjectId || "";

      setInstructorId(String(instId || ""));
      setInstructorObjectId(String(instId || ""));

      const instNombre = detalle.instructorObjectId
        ? [
          detalle.instructorObjectId.nombre1Instructor,
          detalle.instructorObjectId.nombre2Instructor,
          detalle.instructorObjectId.apellido1Instructor,
          detalle.instructorObjectId.apellido2Instructor,
        ]
          .filter(Boolean)
          .join(" ")
        : "";

      setInstructorNombre(instNombre || detalle.nombreCompleto || "");

      setInstructorEmail(
        detalle.instructorObjectId?.emailInstructor ||
        detalle.emailInstructor ||
        ""
      );

      const docJefe =
        detalle.documentoJefe != null ? String(detalle.documentoJefe) : "";

      setDocumentoJefe(docJefe);

      const jefeId = detalle.jefeObjectId?._id || detalle.jefeObjectId || "";

      setJefeObjectId(String(jefeId || ""));

      const jefeNombre = detalle.jefeObjectId
        ? [
          detalle.jefeObjectId.nombre1Jefe,
          detalle.jefeObjectId.nombre2Jefe,
          detalle.jefeObjectId.apellido1Jefe,
          detalle.jefeObjectId.apellido2Jefe,
        ]
          .filter(Boolean)
          .join(" ")
        : "";

      setNombreJefe(jefeNombre || detalle.nombreJefe || "");

      setTelefono1Jefe(
        detalle.jefeObjectId?.telefono1Jefe || detalle.telefono1Jefe || ""
      );

      setCorreoJefe(
        detalle.jefeObjectId?.correoJefe ||
        detalle.jefeObjectId?.emailJefe ||
        detalle.correoJefe ||
        ""
      );

      setAfiliadoARL(detalle.afiliadoARL || "");

      setNivelRiesgoARL(
        detalle.nivelRiesgoARL != null ? String(detalle.nivelRiesgoARL) : ""
      );

      setRiesgoCorrespondeARL(detalle.riesgoCorrespondeARL || "");
      setCuentaEPP(detalle.cuentaEPP || "");
    } catch (error) {
      console.error("❌ Error cargando datos para editar:", error);
    }
  }, [epracticaId]);

  useEffect(() => {
    if (epracticaId) {
      getData();
    }
  }, [epracticaId, getData]);

  // =======================
  // CARGAR MATRÍCULAS POR DOCUMENTO
  // =======================
  const cargarMatriculasPorDocumento = useCallback(async (doc) => {
    const v = String(doc ?? "").trim();

    if (!v) return;

    try {
      setLoadingMatriculas(true);

      setAprendizInfo(null);
      setAprendizObjectId("");
      setMatriculaObjectId("");
      setCodMatricula("");
      setMatriculaUI("");
      setMatriculasOptions([]);

      const { data } = await axios.get(
        `/api/detallematricula/listbymatriculasdocumento/${v}`,
        authConfig()
      );

      console.log("RESPUESTA DOCUMENTO:", data);

      const aprendizData =
        data?.aprendiz ||
        data?.aprendizEncontrado ||
        data?.datosAprendiz ||
        data?.aprendizObjectId ||
        null;

      if (data?.aprendizId) {
        setAprendizObjectId(String(data.aprendizId));
      } else if (aprendizData?._id) {
        setAprendizObjectId(String(aprendizData._id));
      }

      setDocumentoAprendiz(v);

      if (aprendizData) {
        setAprendizInfo({
          documentoAprendiz:
            aprendizData.documentoAprendiz ||
            data?.documentoAprendiz ||
            v ||
            "",

          nombre1Aprendiz: aprendizData.nombre1Aprendiz || "",
          nombre2Aprendiz: aprendizData.nombre2Aprendiz || "",
          apellido1Aprendiz: aprendizData.apellido1Aprendiz || "",
          apellido2Aprendiz: aprendizData.apellido2Aprendiz || "",
        });
      } else {
        setAprendizInfo({
          documentoAprendiz: v,
          nombre1Aprendiz: "",
          nombre2Aprendiz: "",
          apellido1Aprendiz: "",
          apellido2Aprendiz: "",
        });
      }

      const lista = data?.matriculas || [];

      const options = lista.map((m) => ({
        _id: m._id,
        codMatricula: m.codMatricula,
        fichaGrupo: m.fichaGrupo,
        anioLectivo: m.anioLectivo,
        label: `Matrícula ${m.codMatricula ?? "N/A"} — Ficha ${m.fichaGrupo ?? "N/A"
          } — ${m.anioLectivo ?? ""}`,
      }));

      setMatriculasOptions(options);

      if (options.length === 1) {
        setMatriculaObjectId(options[0]._id);
        setCodMatricula(String(options[0].codMatricula ?? ""));
        setMatriculaUI(options[0].label);
      }

      if (options.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Sin matrículas",
          text: "Este aprendiz no tiene matrículas asociadas.",
        });
      }
    } catch (error) {
      console.error("Error cargando matrículas por documento:", error);

      setAprendizInfo(null);
      setMatriculasOptions([]);
      setAprendizObjectId("");
      setMatriculaObjectId("");
      setCodMatricula("");
      setMatriculaUI("");

      Swal.fire({
        icon: "warning",
        title: "No se pudo cargar",
        text:
          error?.response?.data?.message ||
          "Verifica el documento del aprendiz.",
      });
    } finally {
      setLoadingMatriculas(false);
    }
  }, []);

  // =======================
  // CARGAR MATRÍCULAS POR APRENDIZ ID
  // =======================
  const cargarMatriculasPorAprendizId = useCallback(async (aId) => {
    if (!aId) return;

    try {
      setLoadingMatriculas(true);

      setMatriculaObjectId("");
      setCodMatricula("");
      setMatriculaUI("");

      const { data } = await axios.get(
        `/api/detallematricula/listbymatriculasaprendiz/${aId}`,
        authConfig()
      );

      // =====================================================
      // Matrículas del aprendiz
      // =====================================================

      const lista = data?.matriculas || [];

      const options = lista.map((m) => ({
        _id: m._id,

        codMatricula: m.codMatricula,

        fichaGrupo: m.fichaGrupo,

        anioLectivo: m.anioLectivo,

        label: `Matrícula ${m.codMatricula ?? "N/A"} — Ficha ${m.fichaGrupo ?? "N/A"
          } — ${m.anioLectivo ?? ""}`,
      }));

      setMatriculasOptions(options);

      // =====================================================
      // Autoselección si sólo existe una matrícula
      // =====================================================

      if (options.length === 1) {

        setMatriculaObjectId(options[0]._id);

        setCodMatricula(
          String(options[0].codMatricula ?? "")
        );

        setMatriculaUI(options[0].label);
      }

      // =====================================================
      // Sin matrículas
      // =====================================================

      if (options.length === 0) {

        Swal.fire({
          icon: "warning",
          title: "Sin matrículas",
          text:
            "El aprendiz no tiene matrículas asociadas.",
        });
      }

    } catch (error) {

      console.error(
        "Error cargando matrículas por aprendiz:",
        error
      );

      setMatriculasOptions([]);

      setMatriculaObjectId("");
      setCodMatricula("");
      setMatriculaUI("");

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "No se pudieron cargar las matrículas.",
      });

    } finally {

      setLoadingMatriculas(false);
    }
  }, []);

  useEffect(() => {
    if (!edit && aprendizObjectId && !documentoAprendiz) {
      cargarMatriculasPorAprendizId(aprendizObjectId);
    }
  }, [
    aprendizObjectId,
    cargarMatriculasPorAprendizId,
    documentoAprendiz,
    edit,
  ]);

  // =======================
  // SELECCIONAR MATRÍCULA
  // =======================
  const onPickMatriculaFromInput = (value) => {
    setMatriculaUI(value);

    const opt = matriculasOptions.find((m) => m.label === value);

    if (opt) {
      setMatriculaObjectId(opt._id);
      setCodMatricula(String(opt.codMatricula ?? ""));
    } else {
      setMatriculaObjectId("");
      setCodMatricula("");
    }
  };

  // =======================
  // ALTERNATIVA
  // =======================
  const handleAlternativaChange = (e) => {
    const v = e.target.value;

    setAlternativa(v);
    setSubtipo("");
    setAlternativaEtapaPractica(v);
    setSubtipoAlternativaEtapaPractica("");
  };

  // =======================
  // FECHAS
  // =======================
  const handleInicioChange = (e) => {
    const v = e.target.value;

    setFechaInicioEtapaPractica(v);
    setFechaFinEtapaPractica(v ? addMonthsClamped(v, 6) : "");
  };

  useEffect(() => {
    if (fechaInicioEtapaPractica && (!edit || !fechaFinEtapaPractica)) {
      setFechaFinEtapaPractica(addMonthsClamped(fechaInicioEtapaPractica, 6));
    }
  }, [fechaInicioEtapaPractica, edit, fechaFinEtapaPractica]);

  // =======================
  // LIMPIAR
  // =======================
  const cleanData = useCallback(() => {
    setClasificacionEtapaPractica("publica");

    setAlternativa("");
    setSubtipo("");
    setAlternativaEtapaPractica("");
    setSubtipoAlternativaEtapaPractica("");

    setAprendizInfo(null);
    setDocumentoAprendizUI("");
    setMatriculaUI("");
    setMatriculasOptions([]);

    setAprendizObjectId("");
    setDocumentoAprendiz("");
    setMatriculaObjectId("");
    setCodMatricula("");

    setNitEnteconformador("");
    setNombreEnte("");
    setEnteObjectId("");
    setEnteBloqueadoPorAsociacion(false);

    setDocumentoJefe("");
    setNombreJefe("");
    setEstadoAsignacion("");
    setMensajeAsociacion("");
    setTelefono1Jefe("");
    setCorreoJefe("");
    setJefeObjectId("");

    setDocumentoInstructor("");
    setInstructorNombre("");
    setInstructorEmail("");
    setInstructorId("");
    setInstructorObjectId("");

    setAfiliadoARL("");
    setNivelRiesgoARL("");
    setRiesgoCorrespondeARL("");
    setCuentaEPP("");

    setFechaInicioEtapaPractica("");
    setFechaFinEtapaPractica("");
  }, []);

  // =======================
  // VALIDAR
  // =======================
  const validate = useCallback(() => {
    const missing = [];

    if (!aprendizObjectId) missing.push("Aprendiz por documento");
    if (!matriculaObjectId) missing.push("Matrícula");
    if (!clasificacionEtapaPractica) missing.push("Clasificación de la etapa");
    if (!alternativaEtapaPractica) {
      missing.push("Alternativa de etapa productiva");
    }
    if (!afiliadoARL) missing.push("Afiliación ARL");
    if (!riesgoCorrespondeARL) missing.push("¿Riesgo corresponde?");
    if (!cuentaEPP) missing.push("¿Cuenta con EPP?");

    if (!documentoInstructor || isNaN(Number(documentoInstructor))) {
      missing.push("Documento del instructor válido");
    }

    if (fechaInicioEtapaPractica && fechaFinEtapaPractica) {
      const ini = new Date(`${fechaInicioEtapaPractica}T00:00:00`);
      const fin = new Date(`${fechaFinEtapaPractica}T00:00:00`);

      if (fin < ini) {
        Swal.fire({
          icon: "warning",
          title: "Rango de fechas inválido",
          text: "La fecha de fin no puede ser anterior a la fecha de inicio.",
        });

        return false;
      }
    }

    if (missing.length) {
      Swal.fire({
        icon: "warning",
        title: "Faltan datos obligatorios",
        html: `<ul class="text-start mb-0">${missing
          .map((m) => `<li>${m}</li>`)
          .join("")}</ul>`,
      });

      return false;
    }

    if (!Object.keys(alternativas).includes(alternativaEtapaPractica)) {
      Swal.fire({
        icon: "warning",
        title: "Alternativa inválida",
      });

      return false;
    }

    return true;
  }, [
    afiliadoARL,
    alternativaEtapaPractica,
    alternativas,
    aprendizObjectId,
    clasificacionEtapaPractica,
    cuentaEPP,
    documentoInstructor,
    fechaFinEtapaPractica,
    fechaInicioEtapaPractica,
    matriculaObjectId,
    riesgoCorrespondeARL,
  ]);

  // =======================
  // PAYLOAD INDIVIDUAL
  // =======================
  const buildPayload = useCallback(() => {
    const raw = {
      aprendizObjectId,
      documentoAprendiz: toNumberOrUndef(documentoAprendiz),
      matriculaObjectId,
      codMatricula: toNumberOrUndef(codMatricula),

      enteObjectId,
      nitEnteconformador: toNumberOrUndef(nitEnteconformador),

      instructorObjectId,
      documentoInstructor: toNumberOrUndef(documentoInstructor),

      jefeObjectId,
      documentoJefe: toNumberOrUndef(documentoJefe),

      estadoEtapaPractica,

      fechaInicioEtapaPractica: toISODateUTC(fechaInicioEtapaPractica),
      fechaFinEtapaPractica: toISODateUTC(fechaFinEtapaPractica),

      clasificacionEtapaPractica,

      alternativaEtapaPractica,
      subtipoAlternativaEtapaPractica,

      afiliadoARL,
      nivelRiesgoARL: toNumberOrUndef(nivelRiesgoARL),
      riesgoCorrespondeARL,
      cuentaEPP,
    };

    return Object.fromEntries(
      Object.entries(raw).filter(
        ([_, v]) => !(typeof v === "string" && v.trim() === "")
      )
    );
  }, [
    afiliadoARL,
    alternativaEtapaPractica,
    aprendizObjectId,
    clasificacionEtapaPractica,
    codMatricula,
    cuentaEPP,
    documentoAprendiz,
    documentoInstructor,
    documentoJefe,
    enteObjectId,
    estadoEtapaPractica,
    fechaFinEtapaPractica,
    fechaInicioEtapaPractica,
    jefeObjectId,
    matriculaObjectId,
    nitEnteconformador,
    instructorObjectId,
    nivelRiesgoARL,
    riesgoCorrespondeARL,
    subtipoAlternativaEtapaPractica,
  ]);

  // =======================
  // GUARDAR INDIVIDUAL
  // =======================
  const saveEPractica = useCallback(async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tienes permisos para registrar etapa productiva.",
        "warning"
      );
      return;
    }

    try {
      if (!validate()) return;

      const { data } = await axios.post(
        "/api/etapapractica/add",
        buildPayload(),
        authConfig()
      );

      if (!data?.ok) {
        return Swal.fire({
          icon: "warning",
          title: "No se pudo registrar",
          text: data?.message || "Ocurrió un problema al guardar.",
        });
      }

      cleanData();

      await Swal.fire({
        icon: "success",
        title: "Etapa práctica registrada",
        text: data?.message || "La etapa práctica fue registrada correctamente.",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });

      navigate("/etapaproductiva/listareproductiva");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          error?.response?.data?.message ||
          "Ya existe una etapa práctica para este aprendiz y matrícula.",
        confirmButtonText: "Volver",
      });
    }
  }, [
    buildPayload,
    cleanData,
    navigate,
    validate,
    puedeCrear,
  ]);

  // =======================
  // ACTUALIZAR INDIVIDUAL
  // =======================
  const updateEPractica = useCallback(async () => {
    try {
      if (!validate()) return;

      if (!epracticaId) {
        return Swal.fire({
          icon: "warning",
          title: "ID de etapa no encontrado para actualizar",
        });
      }

      const { data } = await axios.put(
        `/api/etapapractica/update/${epracticaId}`,
        buildPayload(),
        authConfig()
      );

      if (!data?.ok) {
        return Swal.fire({
          icon: "warning",
          title: "No se pudo actualizar",
          text: data?.message || "Ocurrió un problema al actualizar.",
        });
      }

      cleanData();

      await Swal.fire({
        icon: "success",
        title: "Etapa práctica actualizada",
        text: data?.message || "La información fue actualizada correctamente.",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });

      navigate("/etapaproductiva/listareproductiva");
    } catch (error) {
      console.error("❌ Error actualizando etapa práctica:", error);

      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Ocurrió un error inesperado.",
      });
    }
  }, [
    buildPayload,
    cleanData,
    epracticaId,
    navigate,
    validate,
  ]);

  // =======================
  // SUBMIT
  // =======================
  const actions = useCallback(
    (e) => {
      e.preventDefault();

      return edit ? updateEPractica() : saveEPractica();
    },
    [edit, saveEPractica, updateEPractica]
  );

  // =======================
  // VERIFICAR NIT
  // =======================
  const verificarNit = useCallback(async (nit) => {
    if (!nit) return;

    try {
      const { data } = await axios.get(
        `/api/ente/verificar/${encodeURIComponent(nit)}`,
        authConfig()
      );

      if (data?.exists) {

        setNombreEnte(
          data.nombreEnteconformador || ""
        );

        setEnteObjectId(
          data.enteId ||
          data._id ||
          data.enteObjectId ||
          ""
        );

        setEnteBloqueadoPorAsociacion(false);

      } else {

        setNombreEnte("");
        setEnteObjectId("");

        setEnteBloqueadoPorAsociacion(false);
      }

    } catch (error) {

      console.error(
        "Error al verificar el NIT:",
        error
      );

      setNombreEnte("");
      setEnteObjectId("");

      setEnteBloqueadoPorAsociacion(false);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "No se pudo verificar el NIT.",
      });
    }
  }, []);

  // =======================
  // VERIFICAR JEFE
  // =======================
  const verificarJefeActivo = useCallback(async (doc) => {
    if (!doc) return;

    try {
      const { data } = await axios.get(
        `/api/asociarjefes/verificar/${encodeURIComponent(doc)}/Activo`,
        authConfig()
      );

      if (data?.ok && data.exists) {
        setDocumentoJefe(String(doc));

        setNombreJefe(data.nombreJefe || "");

        setEstadoAsignacion(
          data.estadoAsignacion || "Activo"
        );

        setMensajeAsociacion(
          "Asociación activa encontrada."
        );

        const jefeId =
          data.jefeObjectId?._id ||
          data.jefeObjectId ||
          data.asociacion?.jefeObjectId?._id ||
          data.asociacion?.jefeObjectId ||
          data.jefeId ||
          "";

        setJefeObjectId(jefeId || "");

        const tel =
          data.telefono1Jefe ||
          data.asociacion?.jefeObjectId?.telefono1Jefe ||
          "";

        const mail =
          data.correoJefe ||
          data.asociacion?.jefeObjectId?.correoJefe ||
          data.asociacion?.jefeObjectId?.emailJefe ||
          "";

        setTelefono1Jefe(tel);
        setCorreoJefe(mail);

        const enteNombre =
          data.enteConformador ||
          data.asociacion?.enteObjectId?.nombreEnteconformador ||
          "";

        const enteNit =
          data.asociacion?.enteObjectId?.nitEnteconformador ||
          "";

        const enteId =
          data.asociacion?.enteObjectId?._id ||
          data.asociacion?.enteObjectId ||
          "";

        if (enteNombre) {
          setNombreEnte(enteNombre);
        }

        if (enteNit) {
          setNitEnteconformador(String(enteNit));
        }

        if (enteId) {
          setEnteObjectId(String(enteId));
        }

        if (enteNombre || enteNit) {
          setEnteBloqueadoPorAsociacion(true);
        }

      } else {

        setNombreJefe("");
        setEstadoAsignacion("");

        setMensajeAsociacion(
          data?.message ||
          "El jefe no tiene asociación activa."
        );

        setTelefono1Jefe("");
        setCorreoJefe("");
        setJefeObjectId("");

        setEnteBloqueadoPorAsociacion(false);
      }

    } catch (error) {

      console.error(
        "Error al verificar la asociación activa:",
        error
      );

      setNombreJefe("");
      setEstadoAsignacion("");

      setMensajeAsociacion(
        error?.response?.data?.message ||
        "Error al verificar la asociación activa."
      );

      setTelefono1Jefe("");
      setCorreoJefe("");
      setJefeObjectId("");

      setEnteBloqueadoPorAsociacion(false);
    }
  }, []);

  // =======================
  // VERIFICAR INSTRUCTOR
  // =======================
  const verificarInstructor = useCallback(async (doc) => {
    if (!doc) return;

    try {
      const { data } = await axios.get(
        `/api/instructores/verificar/${encodeURIComponent(doc)}`,
        authConfig()
      );

      if (data?.exists) {

        setDocumentoInstructor(String(doc));

        setInstructorNombre(
          data.nombreCompleto || ""
        );

        setInstructorEmail(
          data.emailInstructor || ""
        );

        const instId =
          data.instructorObjectId?._id ||
          data.instructorObjectId ||
          data.instructor?._id ||
          data.instructorId ||
          "";

        setInstructorId(instId || "");
        setInstructorObjectId(instId || "");

      } else {

        setInstructorNombre("");
        setInstructorEmail("");

        setInstructorId("");
        setInstructorObjectId("");
      }

    } catch (error) {

      console.error(
        "Error al verificar el documento del instructor:",
        error
      );

      setInstructorNombre("");
      setInstructorEmail("");

      setInstructorId("");
      setInstructorObjectId("");

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "No se pudo verificar el instructor.",
      });
    }
  }, []);

  const handleDocInstructorChange = (e) => {
    setDocumentoInstructor(e.target.value);
  };

  // =======================
  // UI HELPERS
  // =======================
  const titulo = edit ? "Editar Etapa Práctica" : "Registrar Etapa Práctica";

  const nombreCompletoAprendiz = useMemo(() => {
    if (!aprendizInfo) return "";

    return [
      aprendizInfo.nombre1Aprendiz,
      aprendizInfo.nombre2Aprendiz,
      aprendizInfo.apellido1Aprendiz,
      aprendizInfo.apellido2Aprendiz,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [aprendizInfo]);


  return (
    <div>
      <div>
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h5 className="mt-2 mb-1 fw-semibold">{titulo}</h5>
                </div>

                <small className="text-secondary">
                  Diligencia la información por pestañas. El período se calcula
                  automáticamente (+6 meses).
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                  title="Limpiar"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => navigate(-1)}
                  title="Cancelar"
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={actions}>
            <div>
              {/* APRENDIZ Y MATRÍCULA */}
              <div>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

                  {aprendizInfo && (
                    <div className="d-flex align-items-center flex-wrap gap-4 small">
                      <div className="d-flex align-items-center gap-2">
                        <i className="fa-solid fa-user text-secondary"></i>
                        <span className="fw-semibold">
                          {nombreCompletoAprendiz || "—"}
                        </span>
                      </div>

                      <div className="d-flex align-items-center gap-2 text-muted">
                        <i className="fa-solid fa-id-card"></i>
                        <span>
                          Doc:{" "}
                          <strong>
                            {aprendizInfo.documentoAprendiz ??
                              documentoAprendiz ??
                              "—"}
                          </strong>
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-2 px-md-3">
                  <div className="row g-3 mt-2 mb-3">
                    <div className="col-12 col-lg-6">
                      <div className="border rounded-4 p-3 bg-white h-100">
                        <label className="form-label small fw-semibold">
                          Documento del Aprendiz
                        </label>

                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="fa-solid fa-hashtag"></i>
                          </span>

                          <input
                            type="text"
                            className="form-control"
                            placeholder="Digite documento del aprendiz"
                            value={documentoAprendizUI}
                            onChange={(e) => setDocumentoAprendizUI(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                cargarMatriculasPorDocumento(documentoAprendizUI);
                              }
                            }}
                            onBlur={() => {
                              if (documentoAprendizUI?.trim()) {
                                cargarMatriculasPorDocumento(documentoAprendizUI);
                              }
                            }}
                          />

                          <button
                            type="button"
                            className="btn btn-dark"
                            onClick={() =>
                              cargarMatriculasPorDocumento(documentoAprendizUI)
                            }
                            title="Buscar"
                          >
                            <i
                              className={`fa-solid ${loadingMatriculas
                                ? "fa-spinner fa-spin"
                                : "fa-magnifying-glass"
                                }`}
                            ></i>
                          </button>
                        </div>

                        {!aprendizInfo && (
                          <div className="form-text">
                            Escribe el <strong>Documento</strong> para cargar las matrículas.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-lg-6">
                      <div className="border rounded-4 p-3 bg-white h-100">
                        <label className="form-label small fw-semibold">
                          Matrícula
                        </label>

                        <input
                          className="form-control"
                          list="matriculasDatalist"
                          placeholder="Empieza a escribir y selecciona una matrícula..."
                          value={matriculaUI}
                          onChange={(e) => onPickMatriculaFromInput(e.target.value)}
                          disabled={loadingMatriculas || matriculasOptions.length === 0}
                        />

                        <datalist id="matriculasDatalist">
                          {matriculasOptions.map((m) => (
                            <option key={m._id} value={m.label} />
                          ))}
                        </datalist>

                        <div className="form-text">
                          Para guardar es obligatoria la <strong>matrícula</strong>.
                        </div>

                        {matriculaUI && !matriculaObjectId && (
                          <div className="alert alert-warning py-2 mt-2 mb-0">
                            <i className="fa-solid fa-triangle-exclamation me-2"></i>
                            Debes seleccionar una matrícula válida desde las sugerencias.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CLASIFICACIÓN */}
              <div className="px-2 px-md-3">
                <div className="border rounded-4 p-3 mb-3 bg-body-tertiary">
                  <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center gap-2 text-center text-md-start">
                    <div className="fw-semibold">
                      <i className="fa-solid fa-shield-halved me-2"></i>
                      Clasificación del documento
                    </div>

                    <span className="small text-secondary">
                      Selección actual:{" "}
                      <strong>{clasificacionEtapaPractica}</strong>
                    </span>
                  </div>

                  {/* Escritorio */}
                  <div
                    className="btn-group w-100 mt-3 d-none d-md-flex"
                    role="group"
                  >
                    <input
                      type="radio"
                      className="btn-check"
                      name="clasificacion"
                      id="opt-publica"
                      autoComplete="off"
                      value="publica"
                      checked={clasificacionEtapaPractica === "publica"}
                      onChange={(e) =>
                        setClasificacionEtapaPractica(e.target.value)
                      }
                    />

                    <label
                      className="btn btn-outline-success"
                      htmlFor="opt-publica"
                    >
                      Pública
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="clasificacion"
                      id="opt-clasificada"
                      autoComplete="off"
                      value="clasificada"
                      checked={clasificacionEtapaPractica === "clasificada"}
                      onChange={(e) =>
                        setClasificacionEtapaPractica(e.target.value)
                      }
                    />

                    <label
                      className="btn btn-outline-primary"
                      htmlFor="opt-clasificada"
                    >
                      Pública Clasificada
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="clasificacion"
                      id="opt-reservada"
                      autoComplete="off"
                      value="reservada"
                      checked={clasificacionEtapaPractica === "reservada"}
                      onChange={(e) =>
                        setClasificacionEtapaPractica(e.target.value)
                      }
                    />

                    <label
                      className="btn btn-outline-warning"
                      htmlFor="opt-reservada"
                    >
                      Pública Reservada
                    </label>
                  </div>

                  {/* Móvil */}
                  <div className="d-flex d-md-none flex-column gap-2 mt-3">
                    <label
                      className={`btn ${clasificacionEtapaPractica === "publica"
                        ? "btn-success"
                        : "btn-outline-success"
                        }`}
                    >
                      <input
                        type="radio"
                        className="d-none"
                        name="clasificacion"
                        value="publica"
                        checked={clasificacionEtapaPractica === "publica"}
                        onChange={(e) =>
                          setClasificacionEtapaPractica(e.target.value)
                        }
                      />
                      Pública
                    </label>

                    <label
                      className={`btn ${clasificacionEtapaPractica === "clasificada"
                        ? "btn-primary"
                        : "btn-outline-primary"
                        }`}
                    >
                      <input
                        type="radio"
                        className="d-none"
                        name="clasificacion"
                        value="clasificada"
                        checked={clasificacionEtapaPractica === "clasificada"}
                        onChange={(e) =>
                          setClasificacionEtapaPractica(e.target.value)
                        }
                      />
                      Pública Clasificada
                    </label>

                    <label
                      className={`btn ${clasificacionEtapaPractica === "reservada"
                        ? "btn-warning"
                        : "btn-outline-warning"
                        }`}
                    >
                      <input
                        type="radio"
                        className="d-none"
                        name="clasificacion"
                        value="reservada"
                        checked={clasificacionEtapaPractica === "reservada"}
                        onChange={(e) =>
                          setClasificacionEtapaPractica(e.target.value)
                        }
                      />
                      Pública Reservada
                    </label>
                  </div>
                </div>
              </div>

              {/* PERÍODO */}
              <div className="px-2 px-md-3">
                <div className="border rounded-4 p-3 mb-3 bg-white">
                  <div className="fw-semibold mb-3 text-center text-md-start">
                    <i className="fa-solid fa-calendar-days me-2"></i>
                    Período de etapa práctica
                  </div>

                  <div className="row g-3 align-items-end justify-content-center justify-content-md-start">
                    <div className="col-12 col-sm-6 col-lg-3">
                      <label className="form-label small fw-semibold">
                        Fecha Inicio
                      </label>

                      <input
                        type="date"
                        className="form-control"
                        value={fechaInicioEtapaPractica}
                        onChange={handleInicioChange}
                      />
                    </div>

                    <div className="col-12 col-sm-6 col-lg-3">
                      <label className="form-label small fw-semibold">
                        Fecha Fin auto +6 meses
                      </label>

                      <input
                        type="date"
                        className="form-control bg-body-tertiary"
                        value={fechaFinEtapaPractica}
                        readOnly
                        tabIndex={-1}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* TABS */}
              <div className="px-2 px-md-3">
                <div className="bg-white border rounded-4 p-2 shadow-sm">
                  <ul
                    className="nav nav-pills nav-fill flex-column flex-md-row gap-2"
                    id="myTab"
                    role="tablist"
                  >
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active w-100 d-flex align-items-center justify-content-start justify-content-md-center gap-2 rounded-3 py-2"
                        id="EnteCoformador-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#EnteCoformador"
                        type="button"
                        role="tab"
                      >
                        <i className="fa-solid fa-building"></i>
                        <span>Ente / Jefe</span>
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link w-100 d-flex align-items-center justify-content-start justify-content-md-center gap-2 rounded-3 py-2"
                        id="InstructorAsignado-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#InstructorAsignado"
                        type="button"
                        role="tab"
                      >
                        <i className="fa-solid fa-user-tie"></i>
                        <span>Instructor</span>
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link w-100 d-flex align-items-center justify-content-start justify-content-md-center gap-2 rounded-3 py-2"
                        id="Alternativa-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#Alternativa"
                        type="button"
                        role="tab"
                      >
                        <i className="fa-solid fa-list-check"></i>
                        <span>Alternativa</span>
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link w-100 d-flex align-items-center justify-content-start justify-content-md-center gap-2 rounded-3 py-2"
                        id="RiesgosLaborales-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#RiesgosLaborales"
                        type="button"
                        role="tab"
                      >
                        <i className="fa-solid fa-helmet-safety"></i>
                        <span>Riesgos</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className="tab-content mx-2 mx-md-3 p-2 p-md-3 border border-top-0 bg-white rounded-bottom-4 mb-3 mt3"
                id="myTabContent"
              >
                {/* ENTE / JEFE */}
                <div
                  className="tab-pane fade show active"
                  id="EnteCoformador"
                  role="tabpanel"
                >
                  <div className="row g-3">
                    <div className="col-md-2">
                      <label className="form-label small">Documento Jefe</label>

                      <input
                        type="text"
                        className="form-control"
                        value={documentoJefe}
                        onChange={(e) => setDocumentoJefe(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            verificarJefeActivo(e.target.value);
                          }
                        }}
                        onBlur={(e) => verificarJefeActivo(e.target.value)}
                        placeholder="Ej: 12345678"
                      />
                    </div>

                    <div className="col-md-5">
                      <label className="form-label small">Nombre del Jefe</label>

                      <input
                        type="text"
                        className="form-control"
                        value={nombreJefe}
                        onChange={(e) => setNombreJefe(e.target.value)}
                        placeholder="Se autollenará si tiene asociación activa"
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">Teléfono Jefe</label>

                      <input
                        type="text"
                        className="form-control"
                        value={telefono1Jefe}
                        onChange={(e) => setTelefono1Jefe(e.target.value)}
                        placeholder="Teléfono"
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label small">Email Jefe</label>

                      <input
                        type="email"
                        className="form-control"
                        value={correoJefe}
                        onChange={(e) => setCorreoJefe(e.target.value)}
                        placeholder="correo@dominio.com"
                      />
                    </div>

                    <div className="col-12">
                      {mensajeAsociacion && (
                        <div
                          className={`alert ${estadoAsignacion === "Activo"
                            ? "alert-success"
                            : "alert-warning"
                            } py-2 mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2`}
                        >
                          <div>
                            <i className="fa-solid fa-circle-info me-2"></i>
                            <strong>{mensajeAsociacion}</strong>
                          </div>

                          {estadoAsignacion && (
                            <span className="badge bg-dark">
                              Estado: {estadoAsignacion}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small">
                        Nit Empresa{" "}
                        {enteBloqueadoPorAsociacion && (
                          <span className="badge bg-secondary ms-1">
                            Bloqueado
                          </span>
                        )}
                      </label>

                      <input
                        type="number"
                        className="form-control"
                        value={nitEnteconformador}
                        onChange={(e) => setNitEnteconformador(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            verificarNit(e.target.value);
                          }
                        }}
                        onBlur={(e) => verificarNit(e.target.value)}
                        placeholder="Ej: 900123456"
                        disabled={enteBloqueadoPorAsociacion}
                      />
                    </div>

                    <div className="col-md-10">
                      <label className="form-label small">
                        Nombre del Ente{" "}
                        {enteBloqueadoPorAsociacion && (
                          <span className="badge bg-secondary ms-1">
                            Bloqueado
                          </span>
                        )}
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        value={nombreEnte}
                        onChange={(e) => setNombreEnte(e.target.value)}
                        disabled={enteBloqueadoPorAsociacion}
                      />

                      <input type="hidden" value={enteObjectId} readOnly />

                      {enteBloqueadoPorAsociacion && (
                        <div className="form-text">
                          El Ente proviene de la asociación activa del jefe. Para
                          editarlo, cambia o elimina el jefe y vuelve a verificar.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* INSTRUCTOR */}
                <div
                  className="tab-pane fade"
                  id="InstructorAsignado"
                  role="tabpanel"
                >
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label small">
                        Documento Instructor
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        value={documentoInstructor}
                        onChange={handleDocInstructorChange}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            verificarInstructor(e.target.value);
                          }
                        }}
                        onBlur={(e) => verificarInstructor(e.target.value)}
                        placeholder="Ej: 1077866875"
                      />

                      <input type="hidden" value={instructorId} readOnly />
                    </div>

                    <div className="col-md-5">
                      <label className="form-label small">
                        Nombre Instructor
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        value={instructorNombre}
                        onChange={(e) => setInstructorNombre(e.target.value)}
                        placeholder="Nombre del instructor"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small">
                        Email Instructor
                      </label>

                      <input
                        type="email"
                        className="form-control"
                        value={instructorEmail}
                        onChange={(e) => setInstructorEmail(e.target.value)}
                        placeholder="correo@dominio.com"
                      />
                    </div>

                    <div className="col-12">
                      <div className="alert alert-light border py-2 mb-0">
                        <i className="fa-solid fa-circle-question me-2"></i>
                        Tip: escribe el documento y presiona{" "}
                        <strong>TAB</strong> para autocompletar.
                      </div>
                    </div>
                  </div>
                </div>

                {/* ALTERNATIVA */}
                <div className="tab-pane fade" id="Alternativa" role="tabpanel">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small">
                        Alternativa de Etapa Productiva
                      </label>

                      <select
                        className="form-select"
                        value={alternativa}
                        onChange={handleAlternativaChange}
                      >
                        <option value="">-- Seleccione --</option>

                        {Object.keys(alternativas).map((alt) => (
                          <option key={alt} value={alt}>
                            {alt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small">
                        Subtipo de Alternativa
                      </label>

                      <select
                        className="form-select"
                        value={subtipo}
                        onChange={(e) => {
                          setSubtipo(e.target.value);
                          setSubtipoAlternativaEtapaPractica(e.target.value);
                        }}
                        disabled={!alternativa}
                      >
                        <option value="">-- Seleccione --</option>

                        {alternativa &&
                          alternativas[alternativa].map((sub) => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                      </select>

                      {!alternativa && (
                        <div className="form-text">
                          Primero selecciona una alternativa.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIESGOS */}
                <div
                  className="tab-pane fade"
                  id="RiesgosLaborales"
                  role="tabpanel"
                >
                  <div className="card border-0">
                    <div className="card-header bg-dark text-white text-center fw-semibold rounded-3">
                      Decreto 055 de 2015 - Riesgos Laborales
                    </div>

                    <div className="card-body px-0 pt-3">
                      <div className="alert alert-secondary text-center py-2 mb-3">
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        Este espacio debe ser siempre diligenciado.
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Afiliado a ARL?
                          </label>

                          <select
                            className="form-select"
                            value={afiliadoARL}
                            onChange={(e) => setAfiliadoARL(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            Nivel de riesgo
                          </label>

                          <select
                            className="form-select"
                            value={nivelRiesgoARL}
                            onChange={(e) => setNivelRiesgoARL(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="1">Nivel 1</option>
                            <option value="2">Nivel 2</option>
                            <option value="3">Nivel 3</option>
                            <option value="4">Nivel 4</option>
                            <option value="5">Nivel 5</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Riesgo corresponde?
                          </label>

                          <select
                            className="form-select"
                            value={riesgoCorrespondeARL}
                            onChange={(e) =>
                              setRiesgoCorrespondeARL(e.target.value)
                            }
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label small fw-semibold">
                            ¿Cuenta con EPP?
                          </label>

                          <select
                            className="form-select"
                            value={cuentaEPP}
                            onChange={(e) => setCuentaEPP(e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                            <option value="NA">NA</option>
                          </select>
                        </div>
                      </div>

                      <div className="alert alert-light border mt-3 mb-0">
                        <i className="fa-solid fa-circle-info me-2"></i>
                        Recuerda: si el aprendiz no requiere EPP, selecciona{" "}
                        <strong>NA</strong>.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="border-top bg-white px-3 py-3 d-flex justify-content-end gap-2 sticky-bottom">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => navigate(-1)}
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>

                <button type="submit" className="btn btn-dark">
                  <i className="fa-solid fa-floppy-disk me-2"></i>
                  {edit ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrarEProductiva;